import { gql, useMutation, useQuery } from "@apollo/client";
import type { ApolloCache } from "@apollo/client";

// ── GraphQL Documents ─────────────────────────────────────

export const GET_BRANDING = gql`
  query Branding {
    branding {
      logoUrl
      faviconUrl
      footerLogoUrl
      emailLogoUrl
      emailBranding
      primaryColor
      secondaryColor
      backgroundColor
      tertiaryColor
      primaryBgColor
      secondaryBgColor
      tertiaryBgColor
      headingFont
      bodyFont
      hasUnpublishedChanges
    }
  }
`;

export const UPDATE_BRANDING = gql`
  mutation UpdateBranding($input: BrandingInput!) {
    updateBranding(input: $input) {
      logoUrl
      faviconUrl
      footerLogoUrl
      emailLogoUrl
      emailBranding
      primaryColor
      secondaryColor
      backgroundColor
      tertiaryColor
      primaryBgColor
      secondaryBgColor
      tertiaryBgColor
      headingFont
      bodyFont
      hasUnpublishedChanges
    }
  }
`;

export const PUBLISH_BRANDING = gql`
  mutation PublishBranding {
    publishBranding {
      logoUrl
      faviconUrl
      footerLogoUrl
      emailLogoUrl
      emailBranding
      primaryColor
      secondaryColor
      backgroundColor
      tertiaryColor
      primaryBgColor
      secondaryBgColor
      tertiaryBgColor
      headingFont
      bodyFont
      hasUnpublishedChanges
    }
  }
`;

export const REVERT_BRANDING_DRAFT = gql`
  mutation RevertBrandingDraft {
    revertBrandingDraft {
      logoUrl
      faviconUrl
      footerLogoUrl
      emailLogoUrl
      emailBranding
      primaryColor
      secondaryColor
      backgroundColor
      tertiaryColor
      primaryBgColor
      secondaryBgColor
      tertiaryBgColor
      headingFont
      bodyFont
      hasUnpublishedChanges
    }
  }
`;

// ── TypeScript types ─────────────────────────────────────

export interface BrandingData {
  branding: {
    logoUrl: string | null;
    faviconUrl: string | null;
    footerLogoUrl: string | null;
    emailLogoUrl: string | null;
    emailBranding: string | null;
    primaryColor: string | null;
    secondaryColor: string | null;
    backgroundColor: string | null;
    tertiaryColor: string | null;
    primaryBgColor: string | null;
    secondaryBgColor: string | null;
    tertiaryBgColor: string | null;
    headingFont: string | null;
    bodyFont: string | null;
    hasUnpublishedChanges: boolean | null;
  } | null;
}

export interface BrandingInput {
  logoUrl?: string | null;
  faviconUrl?: string | null;
  footerLogoUrl?: string | null;
  emailLogoUrl?: string | null;
  emailBranding?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  backgroundColor?: string | null;
  tertiaryColor?: string | null;
  primaryBgColor?: string | null;
  secondaryBgColor?: string | null;
  tertiaryBgColor?: string | null;
  headingFont?: string | null;
  bodyFont?: string | null;
}

export interface UpdateBrandingVariables {
  input: BrandingInput;
}

export interface UpdateBrandingData {
  updateBranding: {
    logoUrl: string | null;
    faviconUrl: string | null;
    footerLogoUrl: string | null;
    emailLogoUrl: string | null;
    emailBranding: string | null;
    primaryColor: string | null;
    secondaryColor: string | null;
    backgroundColor: string | null;
    tertiaryColor: string | null;
    primaryBgColor: string | null;
    secondaryBgColor: string | null;
    tertiaryBgColor: string | null;
    headingFont: string | null;
    bodyFont: string | null;
    hasUnpublishedChanges: boolean | null;
  } | null;
}

// ── React hooks ─────────────────────────────────────

export function useBrandingQuery() {
  return useQuery<BrandingData>(GET_BRANDING);
}

export function useUpdateBrandingMutation() {
  return useMutation<UpdateBrandingData, UpdateBrandingVariables>(UPDATE_BRANDING, {
    // The Branding type has no `id`, so Apollo can't normalize the mutation
    // result into the `branding` query automatically. Write it back by hand so
    // every consumer (shell, branding page, sidebar) re-renders with the change.
    update(cache, { data }) {
      if (!data?.updateBranding) return;
      cache.writeQuery<BrandingData>({
        query: GET_BRANDING,
        data: { branding: data.updateBranding },
      });
    },
  });
}

/**
 * Publish and revert both return the row the `branding` query reads, so they
 * reuse the same hand-written cache write as updateBranding -- see above for
 * why Apollo can't do it automatically.
 *
 * Note what the two do to `hasUnpublishedChanges`: publish copies draft onto
 * published so the server returns false, and revert copies published back onto
 * draft so it returns false too. Writing the whole payload back means the
 * status pill follows the server rather than being guessed at on the client.
 */
function writeBrandingToCache(
  cache: ApolloCache<unknown>,
  branding: BrandingData["branding"],
) {
  if (!branding) return;
  cache.writeQuery<BrandingData>({ query: GET_BRANDING, data: { branding } });
}

export function usePublishBrandingMutation() {
  return useMutation<{ publishBranding: BrandingData["branding"] }>(PUBLISH_BRANDING, {
    update(cache, { data }) {
      writeBrandingToCache(cache, data?.publishBranding ?? null);
    },
  });
}

export function useRevertBrandingDraftMutation() {
  return useMutation<{ revertBrandingDraft: BrandingData["branding"] }>(
    REVERT_BRANDING_DRAFT,
    {
      update(cache, { data }) {
        writeBrandingToCache(cache, data?.revertBrandingDraft ?? null);
      },
    },
  );
}
